/**
 * A KAMERA ÉLETCIKLUSA — megnyitás, leállítás, hibák.
 *
 * ── MIÉRT SAJÁT KAMERA, ÉS NEM `<input capture>` ────────────────────────
 *
 * A `FilePicker` fejléce már kimondja: a `multiple` és a `capture` együtt nem
 * megy, és böngészőnként másképp sül el. Egy FOLYAMATOS többoldalas
 * fotózáshoz — lapozok, kattintok, lapozok — a fájlválasztós út
 * használhatatlan: minden oldalnál kilép a kameraalkalmazásba és vissza.
 *
 * Ez a hook az ára: a kamera a MI felületünkön fut, tehát a jogosultságot, a
 * leállítást és a hibákat is nekünk kell kezelni.
 *
 * ── A LEÁLLÍTÁS NEM ELHANYAGOLHATÓ RÉSZLET ──────────────────────────────
 *
 * ⚠️ Egy le nem állított `MediaStream` égve hagyja a kamera LED-jét, és
 * viszi az akkumulátort — a felhasználó pedig azt látja, hogy az alkalmazás
 * akkor is figyeli, amikor becsukta. Ez nem teljesítmény-kérdés, hanem
 * bizalmi: a PWA-t egy könyvelőiroda nevében nyitja meg.
 *
 * Ezért a leállítás HÁROM helyen fut le: a `stop()`-ban, a komponens
 * lebontásakor, és akkor is, ha a megnyitás KÖZBEN bontották le (a `getUserMedia`
 * ígérete ilyenkor egy stream-mel tér vissza, amire már senki nem vár).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { type CameraErrorKey, cameraErrorKey } from './camera-errors';

/**
 * ⚠️ A FELBONTÁST KÉRJÜK, NEM KÖVETELJÜK (`ideal`, nem `exact`).
 *
 * Egy `exact` kérés `OverconstrainedError`-t dob azon a készüléken, ami nem
 * tudja — és akkor egyáltalán nincs kamera, ahelyett hogy lenne egy kisebb.
 * A kinyerésnek a felbontás számít ugyan (az apró betűs sorok alacsony
 * felbontáson elvesznek), de egy gyengébb kép még mindig végtelenül több,
 * mint egy hibaüzenet.
 */
const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  // A HÁTLAPI kamera: a papír az asztalon van, nem az arca előtt.
  facingMode: { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

export interface CameraStream {
  /**
   * A `<video>`-ra kötendő ref.
   *
   * `RefObject<HTMLVideoElement>`, NEM `MutableRefObject<… | null>`: a React
   * `ref` propja az előbbit várja, és a `useRef<T>(null)` pont azt adja. A
   * `| null` alak a `tsc`-n bukott el — a vitest nem fogta volna meg.
   */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Fut-e a kamera. */
  active: boolean;
  /** `null`, amíg nincs hiba. */
  error: CameraErrorKey | null;
  /** Újrapróbálkozás a hibaképernyőről. */
  retry: () => void;
}

export function useCameraStream(enabled: boolean): CameraStream {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [active, setActive] = useState(false);
  const [error, setError] = useState<CameraErrorKey | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setError(null);
    setAttempt((value) => value + 1);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: az `attempt` SZÁNDÉKOS kiváltó, nem olvasott érték — a `retry()` ezzel indítja újra a kamerát a hibaképernyőről. A szabály csak azt látja, hogy a törzs nem olvassa; a szerepe pont az, hogy a hatás ÚJRAFUSSON.
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // A LEBONTÁS-JELZŐ. A `getUserMedia` ígérete akkor is megérkezik, ha a
    // komponens közben eltűnt — és a stream ilyenkor FUT. Enélkül a kamera
    // égve maradna egy olyan képernyő után, amit a felhasználó már bezárt.
    let cancelled = false;

    /**
     * A MEGÉRKEZETT STREAM BEKÖTÉSE — vagy leállítása, ha közben lebontottuk.
     *
     * Külön függvény, mert az `open`-be olvasztva átlépte a komplexitási
     * határt; de a szétválasztás amúgy is a helyes olvasat: az egyik a
     * KÉRÉS, a másik az, amit a válasszal kezdünk.
     */
    const attach = (stream: MediaStream) => {
      if (cancelled) {
        stopTracks(stream);
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setActive(true);
    };

    const open = async () => {
      // A `mediaDevices` hiánya nem kivétel, hanem `undefined`: régi
      // böngészőn, és nem biztonságos eredeten. A `?.` nélkül itt egy nyers
      // `TypeError` szállna el, amit a hibakezelő „ismeretlenként" mutatna.
      const devices = navigator.mediaDevices;
      if (!devices?.getUserMedia) {
        setError('denied');
        return;
      }

      try {
        const stream = await devices.getUserMedia({ video: VIDEO_CONSTRAINTS, audio: false });
        attach(stream);
      } catch (caught) {
        if (!cancelled) {
          setError(cameraErrorKey(caught));
        }
      }
    };

    void open();

    return () => {
      cancelled = true;
      setActive(false);
      if (streamRef.current) {
        stopTracks(streamRef.current);
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [enabled, attempt]);

  return { videoRef, active, error, retry };
}

/** Minden sávot külön kell leállítani — a `MediaStream`-nek nincs `stop()`-ja. */
function stopTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}
